import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/DoctorManagement.css';
import '../styles/theme/toast.css';
import medicoService from '../services/medicoService';
import { getAllHospitales } from '../services/hospitalService';
import { getEspecialidadesMedico } from '../services/especialidadMedicoService';

const DoctorManagement = ({ createMode = false }) => {
    const navigate = useNavigate();
    const [doctors, setDoctors] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hospitales, setHospitales] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(createMode);
    const [currentDoctor, setCurrentDoctor] = useState(null);
    const [formData, setFormData] = useState({
        cedula: '',
        nombres: '',
        apellidos: '',
        hospitalId: '',
        telefono: '',
        direccion: '',
        especialidadId: '',
        estado: 'Activo'
    });
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Cargar médicos, hospitales y especialidades
    useEffect(() => {
        fetchDoctors(page, pageSize);
        fetchHospitales();
        fetchEspecialidades();
    }, []);

    useEffect(() => {
        // refetch cuando cambian página o tamaño
        fetchDoctors(page, pageSize);
    }, [page, pageSize]);

    const fetchDoctors = async (p = 1, ps = 10) => {
        try {
            const response = await medicoService.getAllMedicos(p, ps);
            // response may be either an axios response (paginated) or an array (legacy)
            if (response?.data && Array.isArray(response.data.data)) {
                // paginated shape
                setDoctors(response.data.data || []);
                setTotal(Number(response.data.total) || 0);
                setTotalPages(Math.max(1, Math.ceil((Number(response.data.total) || 0) / Number(response.data.pageSize || ps))));
            } else if (Array.isArray(response)) {
                setDoctors(response);
                setTotal(response.length);
                setTotalPages(1);
            } else if (Array.isArray(response?.data)) {
                setDoctors(response.data);
                setTotal(response.data.length);
                setTotalPages(1);
            } else {
                setDoctors([]);
                setTotal(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error al cargar médicos:', error);
            setDoctors([]);
            setTotal(0);
            setTotalPages(1);
        }
    };

    const fetchHospitales = async () => {
        try {
            const response = await getAllHospitales();
            setHospitales(response.data);
        } catch (error) {
            console.error('Error al cargar hospitales:', error);
        }
    };

    const fetchEspecialidades = async () => {
        try {
            const response = await getEspecialidadesMedico();
            setEspecialidades(response.data);
        } catch (error) {
            console.error('Error al cargar especialidades:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.cedula.trim()) newErrors.cedula = 'La cédula es requerida';
        if (!/^[0-9]{10}$/.test(formData.cedula.trim())) newErrors.cedula = 'La cédula debe tener 10 dígitos';
        
        if (!formData.nombres.trim()) newErrors.nombres = 'Los nombres son requeridos';
        if (!formData.apellidos.trim()) newErrors.apellidos = 'Los apellidos son requeridos';
        if (!formData.hospitalId) newErrors.hospitalId = 'El hospital es requerido';
        if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es requerido';
        if (!formData.direccion.trim()) newErrors.direccion = 'La dirección es requerida';
        if (!formData.especialidadId) newErrors.especialidadId = 'La especialidad es requerida';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            // Limpiar el teléfono de caracteres especiales (solo números)
            const telefonoLimpio = formData.telefono.replace(/\D/g, '');
            
            const payload = {
                HOSPITAL_ID: formData.hospitalId,
                ESPECIALIDAD_ID: formData.especialidadId,
                MEDICO_NOMBRE: formData.nombres,
                MEDICO_APELLIDO: formData.apellidos,
                MEDICO_CEDULA: formData.cedula,
                MEDICO_TELEFONO: telefonoLimpio,
                MEDICO_DIRECCION: formData.direccion
            };

            if (currentDoctor) {
                // Actualizar médico existente
                await medicoService.updateMedico(currentDoctor.MEDICO_ID, payload);
                setToast({ show: true, message: '¡Médico actualizado exitosamente!', type: 'success' });
            } else {
                // Crear nuevo médico
                await medicoService.createMedico(payload);
                setToast({ show: true, message: '¡Médico registrado exitosamente!', type: 'success' });
            }
            
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
            
            // Recargar lista de médicos
            await fetchDoctors();
            
            // Cerrar el modal y resetear el formulario
            handleCloseModal();
        } catch (error) {
            console.error('Error al guardar médico:', error);
            alert('Error al guardar el médico. Por favor, intente nuevamente.');
        }
    };

    const handleEdit = (doctor) => {
        setCurrentDoctor(doctor);
        setFormData({
            cedula: doctor.MEDICO_CEDULA || '',
            nombres: doctor.MEDICO_NOMBRE || '',
            apellidos: doctor.MEDICO_APELLIDO || '',
            hospitalId: doctor.HOSPITAL_ID || '',
            telefono: doctor.MEDICO_TELEFONO || '',
            direccion: doctor.MEDICO_DIRECCION || '',
            especialidadId: doctor.ESPECIALIDAD_ID || '',
            estado: doctor.MEDICO_ESTADO || 'Activo'
        });
        setShowForm(true);
    };

    const handleToggleStatus = async (doctor) => {
        const newStatus = doctor.MEDICO_ESTADO === 'Activo' ? 'Desactivado' : 'Activo';
        const message = `¿Está seguro de ${newStatus === 'Activo' ? 'activar' : 'desactivar'} este médico?`;
        
        if (window.confirm(message)) {
            try {
                if (newStatus === 'Activo') {
                    // Si el nuevo estado es Activo, llamamos a activarMedico
                    await medicoService.activarMedico(doctor.MEDICO_ID);
                } else {
                    // Si el nuevo estado es Desactivado, llamamos a deleteMedico
                    await medicoService.deleteMedico(doctor.MEDICO_ID);
                }
                await fetchDoctors();
            } catch (error) {
                console.error('Error al cambiar estado del médico:', error);
                alert('Error al cambiar el estado del médico.');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            cedula: '',
            nombres: '',
            apellidos: '',
            hospitalId: '',
            telefono: '',
            direccion: '',
            especialidadId: '',
            estado: 'Activo'
        });
        setCurrentDoctor(null);
        setErrors({});
    };

    const handleCloseModal = () => {
        setShowForm(false);
        resetForm();
    };

    const filteredDoctors = doctors.filter(doctor =>
        (doctor.MEDICO_NOMBRE || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doctor.MEDICO_APELLIDO || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doctor.MEDICO_CEDULA || '').includes(searchTerm) ||
        (doctor.ESPECIALIDAD || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doctor.HOSPITAL || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            {toast.show && (
                <div className={`custom-toast custom-toast-${toast.type}`}>
                    {toast.message}
                </div>
            )}
            <div className="doctor-management-container">
                <div className="doctor-management-header">
                    <h1>Gestión de Médicos</h1>
                    <p>Administre los médicos del sistema</p>
                </div>

                {!showForm ? (
                    <>
                        <div className="doctor-management-actions">
                            <div className="search-section">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Buscar médico por nombre, cédula, especialidad o hospital..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <button type="button">
                                        <i className="fas fa-search"></i>
                                    </button>
                                </div>
                            </div>
                            <button 
                                className="btn-primary register-btn"
                                onClick={() => setShowForm(true)}
                            >
                                <i className="fas fa-plus"></i> Nuevo Médico
                            </button>
                        </div>

                        <div className="doctors-table-container">
                            <table className="doctors-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Cédula</th>
                                        <th>Nombre Completo</th>
                                        <th>Teléfono</th>
                                        <th>Hospital</th>
                                        <th>Especialidad</th>
                                        <th>Estado</th>
                                        <th>Dirección</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDoctors.map(doctor => (
                                        <tr key={doctor.MEDICO_ID}>
                                            <td>
                                                <div className="doctor-id">{doctor.MEDICO_ID}</div>
                                            </td>
                                            <td>
                                                <div className="doctor-id">{doctor.MEDICO_CEDULA}</div>
                                            </td>
                                            <td>
                                                <div className="doctor-name">
                                                    <div className="doctor-icon">
                                                        <i className="fas fa-user-md"></i>
                                                    </div>
                                                    {`${doctor.MEDICO_NOMBRE || ''} ${doctor.MEDICO_APELLIDO || ''}`.trim()}
                                                </div>
                                            </td>
                                            <td>{doctor.MEDICO_TELEFONO || 'No especificado'}</td>
                                            <td>{doctor.HOSPITAL || 'No asignado'}</td>
                                            <td>{doctor.ESPECIALIDAD || 'No asignada'}</td>
                                            <td>
                                                <span className={`status-badge ${(doctor.MEDICO_ESTADO || 'inactivo').toLowerCase()}`}>
                                                    {doctor.MEDICO_ESTADO || 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>{doctor.MEDICO_DIRECCION || 'No especificada'}</td>
                                            <td>
                                                <div className="actions">
                                                    <button
                                                        className="action-btn edit-btn"
                                                        onClick={() => handleEdit(doctor)}
                                                        title="Editar"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className={`action-btn ${doctor.MEDICO_ESTADO === 'Activo' ? 'delete-btn' : 'activate-btn'}`}
                                                        onClick={() => handleToggleStatus(doctor)}
                                                        title={doctor.MEDICO_ESTADO === 'Activo' ? 'Desactivar' : 'Activar'}
                                                    >
                                                        {doctor.MEDICO_ESTADO === 'Activo' ? <i className="fas fa-ban"></i> : <i className="fas fa-check"></i>}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                                {filteredDoctors.length === 0 && (
                                    <div className="table-footer">
                                        No se encontraron médicos
                                    </div>
                                )}
                        </div>
                            <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div>
                                    Mostrando <strong>{filteredDoctors.length}</strong> de <strong>{total || doctors.length}</strong> médicos
                                </div>
                                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
                                    <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
                                    <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
                                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>Por página:</span>
                                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </label>
                                </div>
                            </div>
                    </>
                ) : null}
                
                {showForm && (
                    <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>
                                    <i className="fas fa-user-md"></i>
                                    {currentDoctor ? 'Editar Médico' : 'Nuevo Médico'}
                                </h3>
                                <button className="modal-close" onClick={handleCloseModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit} className="doctor-form">
                                    <div className="form-section">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="cedula">Cédula</label>
                                    <input
                                        type="text"
                                        id="cedula"
                                        name="cedula"
                                        value={formData.cedula}
                                        onChange={handleInputChange}
                                        className={errors.cedula ? 'error' : ''}
                                        maxLength="10"
                                    />
                                    {errors.cedula && (
                                        <span className="error-message">{errors.cedula}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="especialidadId">Especialidad</label>
                                    <select
                                        id="especialidadId"
                                        name="especialidadId"
                                        value={formData.especialidadId}
                                        onChange={handleInputChange}
                                        className={errors.especialidadId ? 'error' : ''}
                                    >
                                        <option value="">Seleccione una especialidad</option>
                                        {especialidades.map(esp => (
                                            <option key={esp.ESPECIALIDAD_ID} value={esp.ESPECIALIDAD_ID}>
                                                {esp.ESPECIALIDAD_NOMBRE}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.especialidadId && (
                                        <span className="error-message">{errors.especialidadId}</span>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="nombres">Nombres</label>
                                    <input
                                        type="text"
                                        id="nombres"
                                        name="nombres"
                                        value={formData.nombres}
                                        onChange={handleInputChange}
                                        className={errors.nombres ? 'error' : ''}
                                    />
                                    {errors.nombres && (
                                        <span className="error-message">{errors.nombres}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="apellidos">Apellidos</label>
                                    <input
                                        type="text"
                                        id="apellidos"
                                        name="apellidos"
                                        value={formData.apellidos}
                                        onChange={handleInputChange}
                                        className={errors.apellidos ? 'error' : ''}
                                    />
                                    {errors.apellidos && (
                                        <span className="error-message">{errors.apellidos}</span>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="hospitalId">Hospital</label>
                                    <select
                                        id="hospitalId"
                                        name="hospitalId"
                                        value={formData.hospitalId}
                                        onChange={handleInputChange}
                                        className={errors.hospitalId ? 'error' : ''}
                                    >
                                        <option value="">Seleccione un hospital</option>
                                        {hospitales.map(hosp => (
                                            <option key={hosp.HOSPITAL_ID} value={hosp.HOSPITAL_ID}>
                                                {hosp.HOSPITAL_NOMBRE}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.hospitalId && (
                                        <span className="error-message">{errors.hospitalId}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="telefono">Teléfono</label>
                                    <input
                                        type="tel"
                                        id="telefono"
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleInputChange}
                                        className={errors.telefono ? 'error' : ''}
                                    />
                                    {errors.telefono && (
                                        <span className="error-message">{errors.telefono}</span>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="direccion">Dirección</label>
                                    <input
                                        type="text"
                                        id="direccion"
                                        name="direccion"
                                        value={formData.direccion}
                                        onChange={handleInputChange}
                                        className={errors.direccion ? 'error' : ''}
                                    />
                                    {errors.direccion && (
                                        <span className="error-message">{errors.direccion}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="estado">Estado</label>
                                    <select
                                        id="estado"
                                        name="estado"
                                        value={formData.estado}
                                        onChange={handleInputChange}
                                    >
                                        <option value="activo">Activo</option>
                                        <option value="inactivo">Inactivo</option>
                                    </select>
                                </div>
                            </div>
                                    </div>

                                    <div className="form-actions">
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={handleCloseModal}
                                        >
                                            Cancelar
                                        </button>
                                        <button type="submit" className="btn-primary">
                                            {currentDoctor ? 'Guardar Cambios' : 'Crear Médico'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DoctorManagement;